import { addressSchema, arSetupStatusSchema, billedBySchema, dateStringValidation, describedDocumentInput, rentRollBillingSettingsSchema, rentRollSkipReasonSchema, setupFixSchema, syncDirectionSchema, syncStatusSchema, tokenStatusSchema, } from '@teo/types';
import { z } from 'zod';
import { displayId } from './common.js';
export const adminGetLlmCallsSchema = z.object({
    limit: z.number().min(1).max(100).default(20),
    offset: z.number().min(0).default(0),
    templateNameFilter: z.string().optional(),
    modelFilter: z.string().optional(),
    successFilter: z.boolean().optional(),
    sortBy: z.enum(['createdAt', 'latencyMs', 'templateName']).optional(),
    sortOrder: z.enum(['asc', 'desc']).optional(),
});
export const llmCallOutputSchema = z.object({
    id: z.number(),
    templateName: z.string(),
    templateHash: z.string(),
    templateInputs: z.record(z.string(), z.unknown()),
    documentInputs: describedDocumentInput.nullable(),
    rawResponse: z.string().nullable(),
    model: z.string(),
    latencyMs: z.number(),
    tokenCount: z.number().nullable(),
    success: z.boolean(),
    errorMessage: z.string().nullable(),
    userFeedback: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const adminGetLlmCallsOutputSchema = z.object({
    llmCalls: z.array(llmCallOutputSchema),
    total: z.number(),
});
export const adminGetLlmCallSchema = z.object({
    id: z.number(),
});
export const llmCallDetailOutputSchema = llmCallOutputSchema.extend({
    renderedPrompt: z.string().nullable(),
});
export const queueStatsSchema = z.object({
    queueName: z.string(),
    created: z.number(),
    retry: z.number(),
    active: z.number(),
    completed: z.number(),
    expired: z.number(),
    cancelled: z.number(),
    failed: z.number(),
    total: z.number(),
});
export const adminGetTaskQueuesOutputSchema = z.object({
    queues: z.array(queueStatsSchema),
});
export const jobStateSchema = z.enum([
    'created',
    'retry',
    'active',
    'completed',
    'expired',
    'cancelled',
    'failed',
]);
export const taskJobSchema = z.object({
    id: z.string(),
    name: z.string(),
    priority: z.number().optional(),
    data: z.record(z.string(), z.unknown()),
    state: jobStateSchema,
    retryLimit: z.number().optional(),
    retryCount: z.number().optional(),
    retryDelay: z.number().optional(),
    retryBackoff: z.boolean().optional(),
    startAfter: z.string().optional(),
    startedOn: z.string().optional(),
    singletonKey: z.string().optional(),
    singletonOn: z.string().optional(),
    expireIn: z.string().optional(),
    createdOn: z.string(),
    completedOn: z.string().optional(),
    keepUntil: z.string().optional(),
    output: z.record(z.string(), z.unknown()).optional(),
});
export const adminGetTaskJobsSchema = z.object({
    queueName: z.string(),
    limit: z.number().min(1).max(200).default(50),
    state: jobStateSchema.optional(),
});
export const adminGetTaskJobsOutputSchema = z.object({
    jobs: z.array(taskJobSchema),
});
export const adminGetAccountingConnectionsSchema = z.object({
    limit: z.number().min(1).max(100).default(20),
    offset: z.number().min(0).default(0),
    providerFilter: z.string().optional(),
    activeFilter: z.boolean().optional(),
    sortBy: z.enum(['createdAt', 'companyName', 'provider']).optional(),
    sortOrder: z.enum(['asc', 'desc']).optional(),
});
export const accountingConnectionOutputSchema = z.object({
    id: z.number(),
    ownershipId: z.number(),
    provider: z.string(),
    externalCompanyId: z.string(),
    companyName: z.string().nullable(),
    tokenStatus: tokenStatusSchema,
    arSetupStatus: z
        .enum([
        'not_started',
        'draft',
        'syncing_customers',
        'customers_synced',
        'posting_invoices',
        'invoices_posted',
        'live',
    ])
        .nullable(),
    isActive: z.boolean(),
    isSandbox: z.boolean(),
    accessTokenExpiresAt: z.string(),
    refreshTokenExpiresAt: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const adminGetAccountingConnectionsOutputSchema = z.object({
    connections: z.array(accountingConnectionOutputSchema),
    total: z.number(),
});
export const adminGetSyncLogSchema = z.object({
    limit: z.number().min(1).max(100).default(20),
    offset: z.number().min(0).default(0),
    ownershipIdFilter: z.number().optional(),
    entityTypeFilter: z.string().optional(),
    syncStatusFilter: syncStatusSchema,
    syncDirectionFilter: syncDirectionSchema.optional(),
    sortOrder: z.enum(['asc', 'desc']).optional(),
});
export const syncLogOutputSchema = z.object({
    id: z.number(),
    ownershipId: z.number(),
    entityType: z.string(),
    localEntityId: z.string(),
    externalEntityId: z.string().nullable(),
    syncDirection: syncDirectionSchema,
    syncStatus: syncStatusSchema,
    errorMessage: z.string().nullable(),
    createdAt: z.string(),
});
export const adminGetSyncLogOutputSchema = z.object({
    entries: z.array(syncLogOutputSchema),
    total: z.number(),
});
export const adminGetVendorMappingsSchema = z.object({
    limit: z.number().min(1).max(100).default(20),
    offset: z.number().min(0).default(0),
    ownershipIdFilter: z.number().optional(),
    sortOrder: z.enum(['asc', 'desc']).optional(),
});
export const vendorMappingOutputSchema = z.object({
    id: z.number(),
    ownershipId: z.number(),
    contractorName: z.string(),
    externalVendorId: z.string(),
    externalVendorName: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const adminGetVendorMappingsOutputSchema = z.object({
    mappings: z.array(vendorMappingOutputSchema),
    total: z.number(),
});
export const adminGetAuthUrlSchema = z.discriminatedUnion('mode', [
    z.object({
        mode: z.literal('connect'),
        ownershipId: z.number(),
        isSandbox: z.boolean(),
    }),
    z.object({
        mode: z.literal('reconnect'),
        ownershipId: z.number(),
    }),
]);
export const adminGetAuthUrlOutputSchema = z.object({
    url: z.string(),
});
export const adminHandleOAuthCallbackSchema = z.object({
    code: z.string(),
    realmId: z.string(),
    state: z.string(),
});
export const adminHandleOAuthCallbackOutputSchema = z.object({
    companyName: z.string(),
    ownershipDisplayId: z.string(),
});
export const adminDisconnectSchema = z.object({
    ownershipId: z.number(),
});
export const ownershipListItemSchema = z.object({
    id: z.number(),
    displayId: z.string(),
    ownershipName: z.string(),
    ownerUserName: z.string().nullable(),
    ownerUserId: z.number().nullable(),
    ownershipProfileType: z.string(),
    associationType: z.string().nullable(),
    ein: z.string(),
    email: z.string(),
    phoneNumber: z.string(),
    mailingAddress: addressSchema,
});
export const adminListOwnershipsOutputSchema = z.object({
    ownerships: z.array(ownershipListItemSchema),
    sandboxAvailable: z.boolean(),
});
export const adminRetriggerBillSyncSchema = z.object({
    invoiceDisplayId: z.string(),
});
export const adminRetriggerBillSyncOutputSchema = z.object({
    success: z.boolean(),
});
export const adminCheckTokenHealthOutputSchema = z.object({
    queued: z.number(),
});
export const adminResetArSetupSchema = z.object({
    ownershipId: z.number(),
});
export const adminResetArSetupOutputSchema = z.object({
    syncLogsDeleted: z.number(),
    customerMappingsDeleted: z.number().optional(),
});
export const accountingConnectionUserOutputSchema = z
    .object({
    provider: z.string(),
    externalCompanyId: z.string(),
    companyName: z.string(),
    isActive: z.boolean(),
    isSandbox: z.boolean(),
})
    .nullable();
export const companyInfoOutputSchema = z.object({
    name: z.string(),
    legalName: z.string().optional(),
    country: z.string().optional(),
    email: z.string().optional(),
});
export const accountingAccountSchema = z.object({
    externalId: z.string(),
    name: z.string(),
    fullyQualifiedName: z.string(),
    accountType: z.string(),
});
export const listExpenseAccountsOutputSchema = z.object({
    accounts: z.array(accountingAccountSchema),
    defaultExpenseAccountId: z.string().nullable(),
});
export const setDefaultExpenseAccountSchema = z.object({
    ownershipId: displayId,
    accountId: z.string(),
});
export const listBillPaymentClearingAccountsOutputSchema = z.object({
    accounts: z.array(accountingAccountSchema),
    billPaymentClearingAccountId: z.string().nullable(),
});
export const setBillPaymentClearingAccountSchema = z.object({
    ownershipId: displayId,
    accountId: z.string(),
});
export const arUnitSummarySchema = z.object({
    unitDisplayId: z.string(),
    unitIdentifier: z.string().nullable(),
    ownerName: z.string(),
    openingBalance: z.number(),
    customerMapped: z.boolean(),
    externalCustomerId: z.string().nullable(),
    invoicePosted: z.boolean(),
    syncError: z.string().nullable(),
});
export const propertyArSummarySchema = z.object({
    propertyId: z.number(),
    propertyDisplayId: z.string(),
    address: z.string(),
    arGoLiveDate: z.string().nullable(),
    units: z.array(arUnitSummarySchema),
});
export const getArSetupStatusOutputSchema = z.object({
    status: arSetupStatusSchema,
    tokenStatus: tokenStatusSchema,
    hasConnection: z.boolean(),
    billsRenters: z.boolean(),
    units: z.array(arUnitSummarySchema),
    properties: z.array(propertyArSummarySchema),
    goLiveDate: z.string().nullable(),
    billedBy: billedBySchema.nullable(),
});
export const updateOpeningBalanceSchema = z.object({
    ownershipId: displayId,
    unitDisplayId: z.string(),
    amount: z.number().min(0, 'Opening balance cannot be negative'),
});
export const syncArCustomersSchema = z.object({
    ownershipId: displayId,
    propertyId: z.number().optional(),
});
export const syncArCustomersOutputSchema = z.object({
    queued: z.boolean(),
});
export const getRentRollSyncUnitsSchema = z.object({
    ownershipId: displayId,
    propertyId: z.number(),
});
export const getRentRollSyncUnitsOutputSchema = z.object({
    current: z.array(z.object({
        unitDisplayId: z.string(),
        leaseStartDate: z.string(),
        leaseEndDate: z.string().nullable(),
    })),
    excluded: z.array(z.object({
        unitDisplayId: z.string(),
        unitIdentifier: z.string().nullable(),
        renterName: z.string(),
        leaseStartDate: z.string(),
        leaseEndDate: z.string().nullable(),
        reason: z.enum(['pending', 'past']),
    })),
});
export const getRentRollRecurringStatusSchema = z.object({
    ownershipId: displayId,
    propertyId: z.number(),
});
export const getRentRollRecurringStatusOutputSchema = z.object({
    setupUnitDisplayIds: z.array(z.string()),
    units: z.array(z.object({
        unitDisplayId: z.string(),
        unitIdentifier: z.string().nullable(),
        renterName: z.string().nullable(),
        rent: z.number(),
        laundryFee: z.number(),
        parkingFee: z.number(),
        storageFee: z.number(),
        petsFee: z.number(),
        otherFee: z.number(),
        schedule: z
            .object({
            billsOnDay: z.number(),
            startDate: z.string(),
            qboRecurringTemplateId: z.string().nullable(),
        })
            .nullable(),
        loggedQboRecurringTemplateId: z.string().nullable(),
    })),
});
export const createRentRollRecurringSchema = z.object({
    ownershipId: displayId,
    propertyId: z.number(),
    settings: rentRollBillingSettingsSchema,
});
export const createRentRollRecurringOutputSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('blocked'), reasons: z.array(z.string()) }),
    z.object({
        status: z.literal('done'),
        results: z.array(z.discriminatedUnion('status', [
            z.object({
                unitDisplayId: z.string(),
                ownerName: z.string(),
                status: z.literal('created'),
            }),
            z.object({
                unitDisplayId: z.string(),
                ownerName: z.string(),
                status: z.literal('skipped'),
                reason: rentRollSkipReasonSchema,
                message: z.string(),
            }),
            z.object({
                unitDisplayId: z.string(),
                ownerName: z.string(),
                status: z.literal('error'),
                message: z.string(),
            }),
        ])),
    }),
]);
export const postOpeningBalancesSchema = z.object({
    ownershipId: displayId,
    propertyId: displayId.optional(),
    goLiveDate: dateStringValidation,
});
export const postOpeningBalancesOutputSchema = z.object({
    queued: z.boolean(),
});
export const setupBlockSchema = z.object({
    text: z.string(),
    fix: setupFixSchema.optional(),
});
export const goLiveValidationOutputSchema = z.discriminatedUnion('valid', [
    z.object({ valid: z.literal(true) }),
    z.object({ valid: z.literal(false), blocks: z.array(setupBlockSchema) }),
]);
export const validateArGoLiveSchema = z.object({
    ownershipId: displayId,
    propertyId: displayId.optional(),
});
export const activateArGoLiveSchema = z.object({
    ownershipId: displayId,
    goLiveDate: dateStringValidation,
    propertyId: displayId.optional(),
});
export const getAgedReceivablesSchema = z.object({
    ownershipId: displayId,
    reportDate: z.string().optional(),
});
const agedReceivablesRowSchema = z.object({
    externalCustomerId: z.string(),
    customerName: z.string(),
    unitDisplayId: z.string().nullable(),
    ownerName: z.string().nullable(),
    current: z.number(),
    days1to30: z.number(),
    days31to60: z.number(),
    days61to90: z.number(),
    days91AndOver: z.number(),
    total: z.number(),
});
const agedReceivablesGrandTotalSchema = z.object({
    current: z.number(),
    days1to30: z.number(),
    days31to60: z.number(),
    days61to90: z.number(),
    days91AndOver: z.number(),
    total: z.number(),
});
export const agedReceivablesOutputSchema = z.object({
    reportDate: z.string(),
    fetchedAt: z.date(),
    rows: z.array(agedReceivablesRowSchema),
    grandTotal: agedReceivablesGrandTotalSchema,
});
export const adminGetCustomerMappingsSchema = z.object({
    limit: z.number().min(1).max(100).default(20),
    offset: z.number().min(0).default(0),
    ownershipIdFilter: z.number().optional(),
    sortOrder: z.enum(['asc', 'desc']).optional(),
});
export const customerMappingOutputSchema = z.object({
    id: z.number(),
    ownershipId: z.number(),
    unitId: z.number(),
    ownerName: z.string(),
    externalCustomerId: z.string(),
    externalCustomerName: z.string(),
    supersededAt: z.string().nullable(),
    turnoverRequestedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const adminGetCustomerMappingsOutputSchema = z.object({
    mappings: z.array(customerMappingOutputSchema),
    total: z.number(),
});
export const adminCreateChartLinesInQboSchema = z.object({
    ownershipId: displayId,
    taxonomyIds: z.array(z.number().int().positive()).min(1).max(200),
});
export const chartLineQboResultSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('ready'),
        taxonomyId: z.number(),
        qboAccountName: z.string(),
        createdInQbo: z.number(),
        mapRowWritten: z.boolean(),
    }),
    z.object({
        status: z.literal('mapped_elsewhere'),
        taxonomyId: z.number(),
        qboAccountName: z.string(),
        existingMapping: z.object({ qboAccountName: z.string(), taxonomyId: z.number() }),
    }),
    z.object({
        status: z.literal('failed'),
        taxonomyId: z.number(),
        error: z.string(),
    }),
]);
export const adminCreateChartLinesInQboOutputSchema = z.object({
    lines: z.array(chartLineQboResultSchema),
});
//# sourceMappingURL=admin.js.map